import React from 'react';
import EnhancedSEO from '../components/EnhancedSEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle, Star, Target, Users, MessageSquare, Calendar, Share2, TrendingUp, Shield, Clock } from 'lucide-react';
import { testimonials, features, pricingPlans, painPoints, stats } from '../data/mock';
import { useCurrency } from '../contexts/CurrencyContext';

const HomePage = () => {
  const { formatPrice } = useCurrency();

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Local Business Heroes",
    "url": "https://localbusinessheroes.net",
    "logo": "https://customer-assets.emergentagent.com/job_localbizheroes/artifacts/792ds0q9_LBH%20Favicon.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+44-20-4634-9070",
      "contactType": "Customer Service",
      "areaServed": "GB",
      "availableLanguage": ["English"]
    },
    "sameAs": [
      "https://www.linkedin.com/company/localbusinessheroes"
    ]
  };

  // Software Application Schema
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Local Business Heroes CRM",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web, iOS, Android",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "297",
      "highPrice": "997",
      "priceCurrency": "GBP",
      "offerCount": "3"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "description": "All-in-one CRM and marketing automation platform for local businesses. Capture leads, automate follow-up, reduce no-shows, and grow your business."
  };

  // Breadcrumbs
  const breadcrumbs = [
    { name: "Home", url: "/" }
  ];
  
  const getIcon = (iconName) => {
    const icons = {
      Target,
      Users,
      MessageSquare,
      Star,
      Calendar,
      Share2
    };
    const Icon = icons[iconName];
    return Icon ? <Icon size={24} /> : <Target size={24} />;
  };

  return (
    <>
      <EnhancedSEO 
        title="Local Business Heroes UK | CRM & Marketing Automation ⭐ Rated 4.9/5"
        description="Rated 4.9/5 by 1,200+ UK businesses. All-in-one CRM and marketing automation. Capture more leads, automate follow-up, grow faster. Start your 14-day trial for just £5."
        keywords="CRM UK, marketing automation UK, local business CRM, lead generation software UK, small business CRM, appointment scheduling software, customer management system UK, business automation, plumber CRM, electrician CRM"
        url="/"
        includeRating={true}
        testimonials={testimonials.slice(0, 5)}
        additionalSchemas={[organizationSchema, softwareSchema]}
        breadcrumbs={breadcrumbs}
      />
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white py-12 md:py-16 lg:py-20 px-4 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1758520144420-3e5b22e9b9a4?w=1920&h=1080&fit=crop&crop=center" 
            alt="Business Success" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, #FF5A3D 2px, transparent 2px), radial-gradient(circle at 75% 75%, #FF5A3D 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Trust Bar */}
          <div className="text-center mb-8">
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-400 mb-6 px-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>5,247 businesses growing</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="text-yellow-400 fill-current" />
                  ))}
                </div>
                <span>4.9/5 from 1,200+ reviews</span>
              </div>
              <div>⚡ Setup in under 5 minutes</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left px-4 lg:px-0">
              {/* Urgency Badge */}
              <div className="inline-flex items-center bg-[#FF5A3D]/20 border border-[#FF5A3D]/30 rounded-full px-3 sm:px-4 py-2 mb-6 max-w-full">
                <div className="w-2 h-2 bg-[#FF5A3D] rounded-full mr-2 flex-shrink-0 animate-pulse"></div>
                <span className="text-xs sm:text-sm font-medium text-[#FF5A3D] truncate">Limited Time: {formatPrice(5)} Trial (Usually {formatPrice(297)})</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent block">
                  Turn Every Lead
                </span>
                <span className="text-[#FF5A3D] relative block mt-2">
                  Into Paying Customers
                  <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] rounded-full"></div>
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 mb-6 leading-relaxed">
                <strong className="text-white">Stop losing 73% of your leads.</strong> Our UK clients capture 40% more leads 
                and convert 60% more customers with zero extra effort.
              </p>

              {/* Social Proof Results */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-8 border border-white/20">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-[#FF5A3D]">40%</div>
                    <div className="text-xs text-gray-400">More Leads</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#FF5A3D]">60%</div>
                    <div className="text-xs text-gray-400">Higher Conversion</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#FF5A3D]">£8.2K</div>
                    <div className="text-xs text-gray-400">Avg Monthly Increase</div>
                  </div>
                </div>
              </div>
              
              {/* CTA Section */}
              <div className="mb-8">
                <Button 
                  asChild
                  className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg sm:text-xl px-6 sm:px-10 py-4 sm:py-5 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-[#FF5A3D]/30 mb-4 w-full sm:w-auto"
                >
                  <a href="/pricing" className="flex items-center justify-center space-x-2">
                    <span>🚀 Get {formatPrice(5)} Trial (Save {formatPrice(292)})</span>
                    <ArrowRight size={20} className="flex-shrink-0" />
                  </a>
                </Button>
                
                <div className="space-y-2">
                  <p className="text-sm text-gray-400 flex items-center justify-center lg:justify-start space-x-2">
                    <CheckCircle size={16} className="text-green-400" />
                    <span>Just {formatPrice(5)} to start</span>
                    <span>•</span>
                    <CheckCircle size={16} className="text-green-400" />
                    <span>Cancel anytime</span>
                    <span>•</span>
                    <CheckCircle size={16} className="text-green-400" />
                    <span>Setup in 5 minutes</span>
                  </p>
                  <div className="flex items-center justify-center lg:justify-start space-x-4">
                    <p className="text-xs text-[#FF5A3D] font-semibold animate-pulse">⏰ Only 47 trials left at this price</p>
                    <div className="hidden sm:flex items-center space-x-1 text-xs">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                      <span className="text-gray-400">12 people viewing now</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Testimonial */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="flex items-start space-x-3">
                  <img src="https://images.unsplash.com/photo-1627161683077-e34782c24d81?w=48&h=48&fit=crop&crop=face" alt="Sarah Mitchell" className="w-12 h-12 rounded-full" />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={12} className="text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <span className="text-xs font-semibold text-yellow-400">Verified Customer</span>
                    </div>
                    <p className="text-sm text-gray-300 mb-2">
                      "Increased bookings by 40% in 3 months. I've got my evenings back!"
                    </p>
                    <p className="text-xs text-gray-400">
                      <strong>Sarah Mitchell</strong> • The Sunshine Salon, Manchester
                    </p>
                  </div>
                </div>
              </div>

              {/* Customer Logos */}
              <div className="space-y-3 mt-6">
                <p className="text-sm text-gray-400">Join 5,000+ growing businesses:</p>
                <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4 opacity-70">
                  <div className="text-xs bg-white/10 px-3 py-1 rounded-full">Manchester Salons</div>
                  <div className="text-xs bg-white/10 px-3 py-1 rounded-full">Birmingham Gyms</div>
                  <div className="text-xs bg-white/10 px-3 py-1 rounded-full">London Consultants</div>
                  <div className="text-xs bg-white/10 px-3 py-1 rounded-full">Leeds Tradies</div>
                </div>
              </div>
            </div>

            {/* Enhanced Dashboard Preview */}
            <div className="relative">
              {/* Floating background elements */}
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#FF5A3D]/10 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
              
              <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-[1.02]">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center space-x-2 mb-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <h3 className="text-lg font-semibold">Live Dashboard</h3>
                  </div>
                  <p className="text-gray-300 text-sm">Real client results from today</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 text-gray-800 shadow-xl">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-bold text-lg">Today's Performance</h4>
                    <Badge className="bg-green-500 text-white text-sm px-3 py-1">↗️ +247% vs last month</Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                      <div className="text-3xl font-bold text-blue-600">£2,847</div>
                      <div className="text-sm text-blue-600 font-medium">Revenue Today</div>
                      <div className="text-xs text-green-600 mt-1">+£1,200 vs yesterday</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
                      <div className="text-3xl font-bold text-green-600">34</div>
                      <div className="text-sm text-green-600 font-medium">New Customers</div>
                      <div className="text-xs text-green-600 mt-1">+18 automated</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded-lg">
                      <span className="font-medium">Automated follow-ups sent</span>
                      <span className="font-bold text-[#FF5A3D]">289 ✅</span>
                    </div>
                    <div className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded-lg">
                      <span className="font-medium">Reviews collected automatically</span>
                      <span className="font-bold text-[#FF5A3D]">12 ⭐</span>
                    </div>
                    <div className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded-lg">
                      <span className="font-medium">Bookings while you slept</span>
                      <span className="font-bold text-[#FF5A3D]">7 🛏️</span>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-gradient-to-r from-[#FF5A3D]/10 to-[#FF8C42]/10 rounded-lg border border-[#FF5A3D]/20">
                    <p className="text-xs text-[#FF5A3D] font-semibold">💡 This could be YOUR business in 14 days</p>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  LIVE
                </div>
                <div className="absolute -bottom-2 -left-2 bg-[#FF5A3D] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  {formatPrice(5)} TRIAL
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Trust Indicators */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Shield size={24} className="text-green-400" />
                </div>
                <div className="text-sm font-semibold">14-Day Money-Back Guarantee</div>
                <div className="text-xs text-gray-400">Risk-free trial, cancel anytime</div>
              </div>
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Users size={24} className="text-blue-400" />
                </div>
                <div className="text-sm font-semibold">5,000+ Happy Customers</div>
                <div className="text-xs text-gray-400">Join successful UK businesses</div>
              </div>
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 bg-[#FF5A3D]/20 rounded-full flex items-center justify-center">
                  <Clock size={24} className="text-[#FF5A3D]" />
                </div>
                <div className="text-sm font-semibold">Setup in 5 Minutes</div>
                <div className="text-xs text-gray-400">Start growing immediately</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 px-4 bg-gray-50 relative overflow-hidden">
        {/* Background Elements */}
        <div className="hidden sm:block absolute top-0 left-0 w-64 h-64 bg-red-100 rounded-full -translate-x-32 -translate-y-32 opacity-50"></div>
        <div className="hidden sm:block absolute bottom-0 right-0 w-96 h-96 bg-orange-100 rounded-full translate-x-48 translate-y-48 opacity-30"></div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Does This Sound Familiar?
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            You're working harder than ever, but your business isn't growing as fast as it should...
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {painPoints.slice(0, 3).map((pain) => (
              <Card key={pain.id} className="p-6 text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="text-red-500" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0E163B] mb-3">{pain.title}</h3>
                  <p className="text-gray-600">{pain.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
            {painPoints.slice(3, 5).map((pain) => (
              <Card key={pain.id} className="p-6 text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="text-red-500" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0E163B] mb-3">{pain.title}</h3>
                  <p className="text-gray-600">{pain.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 bg-white relative">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=1080&fit=crop&crop=center" 
            alt="Business Growth" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              One System. <span className="text-[#FF5A3D]">Complete Growth.</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stop juggling multiple tools. Our all-in-one platform handles everything from lead capture to customer retention.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <Card key={feature.id} className="p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-[#FF5A3D]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#FF5A3D] group-hover:text-white transition-colors">
                    {getIcon(feature.icon)}
                  </div>
                  <h3 className="text-xl font-semibold text-[#0E163B] mb-4">{feature.title}</h3>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                        <CheckCircle size={16} className="text-[#FF5A3D]" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              asChild
              className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              <a href="/pricing">
                Start Your 14-Day Trial for {formatPrice(5)}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Real Results from Real Businesses
            </h2>
            <p className="text-xl text-gray-600">
              See how local business owners are transforming their growth with our system
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="p-6 hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-700 mb-6">
                    "{testimonial.text}"
                  </blockquote>
                  <div className="border-t pt-4">
                    <div className="flex items-center space-x-3">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full bg-gray-200"
                      />
                      <div>
                        <div className="font-semibold text-[#0E163B]">{testimonial.name}</div>
                        <div className="text-sm text-gray-600">{testimonial.business}, {testimonial.location}</div>
                        <Badge className="mt-1 bg-[#FF5A3D] text-white text-xs">{testimonial.results}</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Choose Your Growth Plan
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Try any plan risk-free for 14 days — just {formatPrice(5)} to get started
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan) => (
              <Card key={plan.id} className={`relative p-8 ${plan.popular ? 'ring-2 ring-[#FF5A3D] scale-105' : ''} hover:shadow-xl transition-all duration-300`}>
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#FF5A3D] text-white px-4 py-1">
                    Most Popular
                  </Badge>
                )}
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl mb-4">{plan.icon}</div>
                  <h3 className="text-2xl font-bold text-[#0E163B] mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold text-[#0E163B] mb-2">£{plan.price}</div>
                  <div className="text-gray-600 mb-6">/month</div>
                  <p className="text-gray-600 mb-8">{plan.description}</p>
                  
                  <Button 
                    asChild
                    className={`w-full mb-6 ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-[#0E163B]'
                    } font-semibold py-3 rounded-lg transition-all duration-300`}
                  >
                    <a href="/pricing">
                      Start 14-Day Trial for {formatPrice(5)}
                    </a>
                  </Button>

                  <div className="text-left space-y-3">
                    {plan.features.slice(0, 5).map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <CheckCircle size={16} className="text-[#FF5A3D]" />
                        <span>{feature}</span>
                      </div>
                    ))}
                    {plan.features.length > 5 && (
                      <div className="text-sm text-gray-500 mt-2">
                        +{plan.features.length - 5} more features
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12">
            <p className="text-gray-600 mb-4">All plans include 14-day trial for just {formatPrice(5)}</p>
            <Button asChild variant="outline" className="border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D] hover:text-white">
              <a href="/pricing">View All Features & Pricing →</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Try It Risk-Free for 14 Days
          </h2>
          <p className="text-xl mb-8 opacity-90">
            We're so confident you'll love Local Business Heroes, we'll let you try everything for just {formatPrice(5)}. 
            If you're not completely satisfied, cancel anytime during your trial.
          </p>
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-semibold mb-4">Our Promise to You</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <CheckCircle size={48} className="mx-auto mb-3" />
                <div className="font-semibold">No Setup Fees</div>
                <div className="text-sm opacity-90">Get started immediately</div>
              </div>
              <div>
                <CheckCircle size={48} className="mx-auto mb-3" />
                <div className="font-semibold">Cancel Anytime</div>
                <div className="text-sm opacity-90">No long-term contracts</div>
              </div>
              <div>
                <CheckCircle size={48} className="mx-auto mb-3" />
                <div className="font-semibold">Full Support</div>
                <div className="text-sm opacity-90">We'll help you succeed</div>
              </div>
            </div>
          </div>
          <Button 
            asChild
            className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-lg sm:text-xl px-6 sm:px-10 py-3 sm:py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
          >
            <a href="/pricing">
              Start Growing Your Business Today
            </a>
          </Button>
        </div>
      </section>
    </div>
    </>
  );
};

export default HomePage;